import { useMutation } from "@tanstack/react-query";
import { addTeacherAvailability } from "api/teacher.api.ts";
import { useAuth } from "context/AuthContext";
import { useToast } from "hooks/index.js";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { HelperText } from "ui/base/helperText/HelperText";
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp";
import { hideScrollBar } from "ui/base/scrollbarStyle/scrollbarStyle";
import { Switch } from "ui/base/switch/Switch";
import { CalendarIcon } from "ui/icons/CalendarIcon";
import { CloseIcon } from "ui/icons/inputIcons/CloseIcon.jsx";
import { PlusIcon } from "ui/icons/PlusIcon";
import { defaultThemeColors } from "ui/theme";
import { calculateTimeDifferenceInMinutes, numberOnly } from "utils";
import { AddClassHourModal } from "./AddClassHourModal";
import { ClassDurationModal } from "./ClassDurationModal.jsx";
import {isTimeConflictFromAvailability} from "../../../../../utils/schedule-utils";

const getRelevantDays = (classAvailability, dayIndex) => {
    if (!dayIndex) return;

    const getNextDayIndex = (currentId) => (currentId % 7) + 1;
    const getPreviousDayIndex = (currentId) => ((currentId - 2 + 7) % 7) + 1;

    return [
        ...classAvailability?.filter(
            (day) =>
                day.isActive &&
                (day.index === dayIndex ||
                    day.index === getNextDayIndex(dayIndex) ||
                    day.index === getPreviousDayIndex(dayIndex))
        ),
    ];
};

const getDefaultAvailability = (slots, onlineSlots) => {
    const defaultDays = [
        { day: "Monday", index: 1, isActive: false, schedules: [] },
        { day: "Tuesday", index: 2, isActive: false, schedules: [] },
        { day: "Wednesday", index: 3, isActive: false, schedules: [] },
        { day: "Thursday", index: 4, isActive: false, schedules: [] },
        { day: "Friday", index: 5, isActive: false, schedules: [] },
        { day: "Saturday", index: 6, isActive: false, schedules: [] },
        { day: "Sunday", index: 7, isActive: false, schedules: [] },
    ];

    const combinedByDay = {};

    for (const slot of slots) {
        if (!combinedByDay[slot.day]) {
            combinedByDay[slot.day] = {
                activeStatus: slot.activeStatus,
                teacher_class_timings: [...slot.teacher_class_timings],
            };
        } else {
            combinedByDay[slot.day].teacher_class_timings.push(...slot.teacher_class_timings);
            combinedByDay[slot.day].activeStatus ||= slot.activeStatus;
        }
    }

    for (const onlineSlot of onlineSlots) {
        if (!combinedByDay[onlineSlot.day]) {
            combinedByDay[onlineSlot.day] = {
                activeStatus: onlineSlot.activeStatus,
                teacher_class_timings: [...onlineSlot.teacher_class_timings],
            };
        } else {
            combinedByDay[onlineSlot.day].teacher_class_timings.push(...onlineSlot.teacher_class_timings);
            combinedByDay[onlineSlot.day].activeStatus ||= onlineSlot.activeStatus;
        }
    }

    const savedDays = defaultDays.map((defaultDay) => {
        const data = combinedByDay[defaultDay.day];
        return {
            ...defaultDay,
            isActive: data?.activeStatus || false,
            schedules: data?.teacher_class_timings || [],
        };
    });

    return savedDays.map(day => ({
        ...day,
        schedules: [...day.schedules].sort((a, b) => {
            const timeA = moment(a.time, ["h:mm A", "hh:mm A"]);
            const timeB = moment(b.time, ["h:mm A", "hh:mm A"]);
            return timeA - timeB;
        }),
    }));
};

export const SchedulePage = ({ handleErrors }) => {
    const { userData, refetch } = useAuth();

    const [classDuration, setClassDuration] = useState(
        userData?.teacher?.class_duration || ""
    );

    const [selectedDay, setSelectedDay] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [showDurationModal, setShowDurationModal] = useState(false);

    const [formErrors, setFormErrors] = useState({
        classDuration: "",
    });

    const [classAvailability, setClassAvailability] = useState(
        getDefaultAvailability(
            userData?.teacher?.slots || [],
            userData?.teacher?.online_slots || []
        )
    );

    useEffect(() => {
        setClassDuration(userData?.teacher?.class_duration || "")
    }, [userData?.teacher?.class_duration])


    useEffect(() => {
        setClassAvailability(
            getDefaultAvailability(
                userData?.teacher?.slots || [],
                userData?.teacher?.online_slots || []
            )
        );
    }, [userData?.teacher?.slots, userData?.teacher?.online_slots]);

    const handleClassDurationChange = (value) => {
        setClassDuration(value);
        setFormErrors((prev) => ({
            ...prev,
            classDuration:
                +value < 30 ? "Class duration must be at least 30 minutes" : "",
        }));
    };

    const addTime = (dayIndex, { time, type, online_type }) => {
        setClassAvailability((prevDays) =>
            prevDays.map((day) =>
                day.index === dayIndex
                    ? {
                        ...day,
                        schedules: [...day.schedules, { time, type, online_type }].sort(
                            (a, b) =>
                                moment(a.time, "h:mm A").diff(moment(b.time, "h:mm A"))
                        ),
                    }
                    : day
            )
        );
    };

    const removeTime = (dayIndex, timeIndex) => {
        setClassAvailability((prevDays) =>
            prevDays.map((day) =>
                day.index === dayIndex
                    ? {
                        ...day,
                        schedules: day.schedules.filter((_, i) => i !== timeIndex),
                    }
                    : day
            )
        );
    };

    const toggleDay = (dayIndex) => {
        if (!classDuration || formErrors.classDuration) {
            setFormErrors((prev) => ({ ...prev, classDuration: "Must be filled" }));
            return;
        }
        setClassAvailability((prevDays) =>
            prevDays.map((day, index) =>
                day.index === dayIndex ? { ...day, isActive: !day.isActive } : day
            )
        );
    };

    const applyClassDurationForAllDays = (dayIndex) => {
        setClassAvailability((prevDays) => {
            const selectedDaySchedules = prevDays.find(
                (day) => day.index === dayIndex
            ).schedules;
            return prevDays.map((day) => ({
                ...day,
                schedules: day.isActive ? [...selectedDaySchedules] : day.schedules,
            }));
        });
    };

    const { mutate, isLoading } = useMutation({
        mutationFn: addTeacherAvailability,
        onError: (err) => {
            if (err?.response?.data?.message) {
                useToast(
                    "error",
                    error?.response?.data?.message || "Error: addTeacherAvailability"
                );
            }
        },
        onSuccess: () => {
            useToast("success", "Changes saved");
            handleErrors(null)
            refetch()
        },
    });

    const handleSubmit = () => {
        if (!classDuration) {
            setFormErrors((prev) => ({
                ...prev,
                classDuration: "Must be filled",
            }));
            return;
        }

        const activatedClassAvailability = classAvailability.filter(
            (classAv) => classAv.isActive
        );

        const flattenedClasses = activatedClassAvailability?.flatMap((cls) => {
            return cls.schedules;
        });

        if (flattenedClasses.length < 1) {
            useToast("error", "Add at least 1 class");
            return;
        }

        const isTimeError = classAvailability.some((currentDay) =>
            currentDay.schedules.some((currentSchedule) =>
                isTimeConflictFromAvailability(
                    currentSchedule,
                    currentDay.index,
                    classAvailability,
                    calculateTimeDifferenceInMinutes,
                    classDuration
                )
            )
        );

        if (isTimeError) {
            useToast(
                "error",
                "Separation between classes is shorter than class duration"
            );
            return;
        }

        mutate({
            class_duration: classDuration,
            days: activatedClassAvailability,
        });
    };

    return (
        <>
            <InnerContainer>
                <InputSignUp
                    onInput={numberOnly}
                    label="How long does the class last?"
                    placeholder="Add duration"
                    readOnly
                    inputCenterText
                    placeholderPosition={'center'}
                    value={classDuration ? classDuration + " minutes" : ""}
                    onClick={() => setShowDurationModal(true)}
                    style={{ cursor: "pointer" }}
                    isError={formErrors?.classDuration}
                    errorText={formErrors?.classDuration}
                    inputContainerStyle={{
                        width: "fit-content"
                    }}
                />
                <ClassHourOuterContainer>
                    <HelperText>Classes Hours</HelperText>
                    {formErrors?.classConflict ? (
                        <HelperText color="pink" size="regular">
                            {formErrors.classConflict}
                        </HelperText>
                    ) : null}
                    {classAvailability.map((day) => (
                        <ClassHours
                            key={day.day}
                            day={day}
                            toggleDay={toggleDay}
                            addTime={() => {
                                setSelectedDay(day.index);
                                setShowModal(true);
                            }}
                            removeTime={(dayIndex, timeIndex) =>
                                removeTime(dayIndex, timeIndex)
                            }
                            applyForAll={applyClassDurationForAllDays}
                        />
                    ))}
                </ClassHourOuterContainer>

                <div style={{ width: "100%", display: "flex" }}>
                    <ButtonPrimary
                        disabled={isLoading}
                        onClick={() => {
                            handleSubmit();
                        }}
                    >
                        Save Changes
                    </ButtonPrimary>
                </div>
            </InnerContainer>

            {selectedDay ? (
                <AddClassHourModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    classType={userData?.teacher?.type_of_class}
                    onlineType={userData?.teacher?.online_type}
                    onAddClass={(newClass) => addTime(selectedDay, newClass)}
                    relevantDays={getRelevantDays(classAvailability, selectedDay)}
                    classDuration={classDuration}
                    selectedDay={selectedDay}
                />
            ) : null}

            <ClassDurationModal
                show={showDurationModal}
                handleClose={() => setShowDurationModal(false)}
                classType={userData?.teacher?.type_of_class}
                onAddClassDuration={(value) => handleClassDurationChange(value)}
                classAvailability={classAvailability}
                currentClassDuration={classDuration}
            />
        </>
    );
};

const ClassHours = ({ day, toggleDay, addTime, removeTime, applyForAll }) => {

    return (
        <ClassHourContainer>
            <FlexRow>
                <ClassHourHeaderIconContainer>
                    <CalendarIcon />
                    <HelperText>{day.day}</HelperText>
                </ClassHourHeaderIconContainer>
                <Switch
                    id={day.day}
                    onChange={() => {
                        toggleDay(day.index);
                    }}
                    toggled={day.isActive}
                />
            </FlexRow>
            {day.schedules.length > 0 ? (
                day.isActive ? (
                    <ClassHourItemContainer>
                        {day.schedules.map((schedule, index) => (
                            <CLassHour
                                key={index}
                                {...schedule}
                                removeTime={() => removeTime(day.index, index)}
                            />
                        ))}
                    </ClassHourItemContainer>
                ) : null
            ) : null}

            {day.isActive ? (
                <FlexRow>
                    <FlexRow>
                        <IconContainer onClick={addTime}>
                            <PlusIcon size={20} color="#554DF1" />
                        </IconContainer>
                        <HelperText
                            color="blue"
                            onClick={addTime}
                            style={{ cursor: "pointer" }}
                        >
                            Add class
                        </HelperText>
                    </FlexRow>
                    {day.schedules.length > 0 ? (
                        <HelperText
                            color="blue"
                            style={{ cursor: "pointer" }}
                            onClick={() => applyForAll(day.index)}
                        >
                            Use for all active days
                        </HelperText>
                    ) : null}
                </FlexRow>
            ) : null}
        </ClassHourContainer>
    );
};

const CLassHour = ({ time, online_type, type, removeTime }) => {
    return (
        <ClassHourItem>
            <TextRow>
                <HelperText size="regular">{time}</HelperText>
                <HelperText color="placeholder" size="regular">
                    {online_type === "online" && type === "Private" ? "Online" : type}
                </HelperText>
            </TextRow>

            <ClassHourDelete onClick={removeTime}>
                <CloseIcon />
            </ClassHourDelete>
        </ClassHourItem>
    );
};

const InnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
    gap: 20px;
    grid-column: span 2;
    @media (min-width: 1024px) {
        grid-column: span 1;
    }
    ${hideScrollBar}
`;

const FlexRow = styled.div`
    display: flex;
    justify-content: ${({ justifyContent }) => justifyContent || "space-between"};
    align-items: center;
    gap: 0.5rem;
`;

const ClassHourOuterContainer = styled.div`
    display: grid;
    gap: 1rem;
`;

const ClassHourContainer = styled.div`
    border: 2px solid ${defaultThemeColors.border};
    border-radius: 8px;
    padding: 20px;
    display: grid;
    gap: 20px;
`;

const ClassHourHeaderIconContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
`;

const ClassHourItemContainer = styled.div`
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 1fr;

    @media (min-width: 1024px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const ClassHourItem = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 1rem;
    background-color: ${defaultThemeColors.border};
    border-radius: 8px;
    gap: 1rem;

    @media (min-width: 1024px) {
        justify-content: center;
    }
`;
const TextRow = styled.div`
    display: flex;
    flex: 1;

    align-items: center;

    flex-direction: row;
    gap: 1rem;

    @media (min-width: 1024px) {
        gap: 0.2rem;
        flex-direction: column;
        justify-content: center;
    }
`;

const ClassHourDelete = styled.div``;

const IconContainer = styled.div`
    height: ${({ size }) => size ?? "30px"};
    width: ${({ size }) => size ?? "30px"};
    background-color: ${({ backgroundColor }) =>
        backgroundColor ?? defaultThemeColors.lightBlue};
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 99px;
    color: ${({ color }) => color ?? defaultThemeColors.white};
    cursor: pointer;
`;
